import { useTranslation } from 'react-i18next';

import { Camera } from 'lucide-react';

import { useProfileImage } from './useProfileImage';
import { AvatarProfileIcon } from '../../components';

/**
 * ProfilePhotoField — 프로필 수정 화면 맨 위의 아바타(Figma `MY #프로필 수정` `avatar area`):
 * 88px 원 + 오른쪽 아래의 검은 카메라 배지.
 *
 * 사진 선택은 네이티브만 할 수 있어서, 브라우저에서 열면 **아바타만 보이고 배지가 없다**.
 * 눌러도 안 되는 배지를 두는 것보다 낫다.
 *
 * **고르는 자리는 배지 하나다.** 카메라·앨범을 화면에 나란히 두면 사진 하나 바꾸는 데 두 번을
 * 눌러야 한다. 어디서 가져올지는 OS 가 시트로 묻는다(`pickImage` 를 `source` 없이
 * 부른다) — 사용자가 이미 다른 앱에서 매일 보는 질문이고, 취소도 그 시트가 받는다.
 * 지우기는 사진이 있을 때만 아래에 글자로 선다. 버튼으로 만들면 파괴적인 선택이
 * 사진을 바꾸는 선택과 같은 무게로 보인다.
 *
 * 저장 버튼과 무관하게 **즉시 반영된다.** 화면 아래 설문 답들은 "저장하기"를 눌러야
 * 남지만 사진은 고르는 즉시 저장된다 — 고른 사진이 화면에 이미 보이는데 저장을
 * 또 눌러야 한다면, 안 누르고 나간 사람은 사진이 사라진 이유를 알 수 없다.
 */
export const ProfilePhotoField = () => {
  const { t } = useTranslation();
  const { image, canPick, picking, notice, pick, remove, dismissNotice } =
    useProfileImage();

  return (
    <section className="flex flex-col items-center gap-2 pb-7 pt-5">
      <div className="relative h-[88px] w-[88px]">
        {image ? (
          <img
            src={image}
            alt={t('web.profile.photo.alt')}
            className="h-full w-full rounded-full object-cover"
          />
        ) : (
          <AvatarProfileIcon
            role="img"
            aria-label={t('web.profile.photo.empty')}
            className="h-full w-full"
          />
        )}
        {canPick && (
          <button
            type="button"
            onClick={() => void pick()}
            disabled={picking}
            aria-label={
              image ? t('web.profile.photo.change') : t('web.profile.photo.add')
            }
            // 그려진 배지는 30px, 누르는 자리만 44px 로 넓힌다. 테두리는 페이지 배경색이라
            // 아바타에서 배지를 도려낸 것처럼 보인다.
            className="absolute -bottom-0.5 -right-0.5 flex h-[30px] w-[30px] items-center justify-center rounded-full border-2 border-background bg-black text-white after:absolute after:-inset-[9px] after:content-[''] disabled:opacity-50"
          >
            <Camera className="h-4 w-4" aria-hidden="true" />
          </button>
        )}
      </div>

      {canPick && image && (
        <button
          type="button"
          onClick={remove}
          disabled={picking}
          className="text-xs text-bk-600 underline underline-offset-2"
        >
          {t('web.profile.photo.remove')}
        </button>
      )}

      {/* 사진이 서버로 가지 않는다는 사실은 기기를 바꿀 때 드러난다 — 미리 말해 둔다. */}
      {canPick && (
        <p className="text-xs text-bk-600">
          {t('web.profile.photo.localOnly')}
        </p>
      )}

      {notice && (
        <button
          type="button"
          onClick={dismissNotice}
          className="text-xs text-destructive underline underline-offset-2"
        >
          {t(`web.profile.photo.${notice}`)}
        </button>
      )}
    </section>
  );
};
